import { take, fork, select, put, call, delay, cancel } from 'redux-saga/effects';
import actionTypes from '../actionTypes';
import { createBrowserHistory } from 'history';
import { failLogin } from "../actionCreators/authentication";
import v from "../../validators/GlobalValidator";
import { callApi } from "./callApi";
import parameters from "../../config/parameters";
import { fetchClubsByAccounts, getGlogalAccountData, dataURLtoFile } from './helpers/club';
import { successGlobalAccount } from "../actionCreators/account";
import {
    successCreateClub,
    successClubList,
    failCreateClub,
    successClubsInfo,
    successCurrentClub,
    successDisbandClub,
    successCurrentClubMembers,
    successJoinClub,
    changeClubSuccess,
    clubDataSuccess,
    changeMemberRoleSuccess,
    changeMemberRoleFailure,
    exportDataSuccess,
    exportDataFailure,
} from "../actionCreators/club";
import {
    getClubList,
    getCurrentClubAdminToken, 
    getGlobalAccount,
    getCurrentClubId,
    getGlobalAccountId,
    getGlobalToken,
    getCurrentClubToken,
    getCurrentClubAgentToken,
} from '../selectors';
import config from '../../config/config';
import {showNotification} from "../actionCreators/notification";
import moment from 'moment';
import { successAvatars } from '../actionCreators/avatar';

function* createClub({ name, description, avatar, code }) {
    try {
        let validation = v.validateCreateClub({ username: name });
        if (!validation.success) {
            yield put(showNotification({message: validation.error.username, appearance: 'error'}));
            return;
        }
        const globalAccountId = yield select(getGlobalAccountId);
        const token = yield select(getGlobalToken);
        let params = {
            typeId: parameters.accountTypes.club,
            username: name,
            globalAccountId,
            code,
            token
        };
        const response = yield call(callApi, `${config.API_URL}/club/account`, 'POST', params);
        const {token: patchToken, id: patchId} = response.data.result.authenticationData.find(account => account.typeId === 3);
        yield call(callApi, `${config.API_URL}/club/clubInfo/${patchId}`, 'PATCH', {description, token: patchToken});

        if ( avatar ) {
            const headers = {
                'Content-Type': 'multipart/form-data',
                'x-access-token': patchToken
            };
            const file = dataURLtoFile(avatar);
            const data = new FormData();
            data.append('avatar', file);

            yield call(callApi, `${config.API_MEDIA_URL}/uploadAvatar`, 'POST', data, headers);
        }

        yield put(successCreateClub());
        yield put(showNotification({message: `Successfully created the ${name} club`}));
    } catch (e) {}
}

function* joinClub({clubId, agentId}) {
    try {
        const { id: accountId, token } = yield select(getGlobalAccount);
        const params = {accountId, agentId, token, skinId: clubId};

        yield call(callApi, `${config.API_URL}/club/pendingAccount`, 'POST', params);
        yield put(successJoinClub());
        yield put(showNotification({message: 'Successfully sent the request.'}));
    } catch (e) {}
}

function* getClubsInfo() {
    try {
        const { id, token } = yield select(getGlobalAccount);
        const response = yield call(callApi, `${config.API_URL}/club/account/${id}/clubInfo`, 'GET', { token });
        yield put(successClubsInfo(response.data.result.clubInfo));
    } catch (e) {}
}

function* getCurrentClubMembers() {
    try {
        const token = yield select(getCurrentClubToken);
        const response = yield call(callApi, `${config.API_URL}/club/account`, 'GET', { token });
        yield put(successCurrentClubMembers(response.data.result.accounts));
    } catch (e) {}
}

function* getClubData({ clubId, startDate, endDate, forAgent }) {
    try {
        const token = yield select(getCurrentClubToken);
        const params = { token };
        if (startDate) params.startDate = startDate.format();
        if (endDate) params.endDate = endDate.format();

        const response = yield call(callApi, `${config.API_URL}/club/accounts/report`, 'GET', params);
        yield put(clubDataSuccess(clubId, response.data.result));
    } catch (e) {}
}

function* disbandClub({ clubId, history }) {
    try {
        const token = yield select(getCurrentClubToken);
        // const token = yield select(getCurrentClubAdminToken);
        yield call(callApi, `${config.API_URL}/club/account/${clubId}`, 'DELETE', { token });

        yield put(showNotification({message: `Successfully deleted the club`}));
        yield put(successDisbandClub());
        history.push('/');
    } catch (e) {}
}

function* setCurrentClub(clubId) {
    try {
        const clubList = yield select(getClubList);
        const currentClub = clubList.find(club => club.clubId == clubId);

        // Put level expiration days info
        //
        const today = moment().format('YYYY/MM/DD');
        const expiration = currentClub.levelExp ? moment(currentClub.levelExp).format('YYYY/MM/DD') : 0;
        currentClub.levelExpDays = (moment(expiration).diff(today, 'days') > 0) ? moment(expiration).diff(today, 'days') : 0;
        if (!!currentClub) {
            yield put(successCurrentClub(currentClub));
        }
    } catch(e) {}
}

function* makeClubList( arrAccounts ) {
    try {
        yield put(successGlobalAccount(getGlogalAccountData(arrAccounts)));
        yield put(successClubList(fetchClubsByAccounts(arrAccounts)));

        // dispatch(getTables({ clubs: clubs }))
    } catch (e) {
        yield put(failLogin({ loginError: e }));
    }
}

function* changeClubDetails ({ clubDescription, clubAvatar }) {
    try {
        const token = yield select(getCurrentClubAdminToken);
        const clubId = yield select(getCurrentClubId);

        if ( !!clubDescription ) {
            //also here should be handled club name change but it is disabled for now as before
            yield call(callApi, `${config.API_URL}/club/clubInfo/${clubId}`, 'PATCH', { description: clubDescription, token });
        }

        if ( clubAvatar ) {
            const headers = {
                'Content-Type': 'multipart/form-data',
                'x-access-token': token
            };
            const file = dataURLtoFile(clubAvatar.data);
            const data = new FormData();
            data.append('avatar', file);

            const response = yield call(callApi, `${config.API_MEDIA_URL}/uploadAvatar`, 'POST', data, headers);
            yield put(successAvatars({ [clubId]: response.data.result.account }));
        }
        yield put(showNotification({message: 'Successfully updated the club.'}));
        yield put(changeClubSuccess({clubId, description: clubDescription}));

    } catch (e) {}
}

function* changeMemberRole({ memberId, roleId }) {
    try {
        const token = yield select(getCurrentClubAdminToken);
        const clubId = yield select(getCurrentClubId);

        yield call(
            callApi,
            `${config.API_URL}/club/account/${memberId}/role`,
            'PATCH',
            { typeId: roleId, skinId: clubId },
            { 'x-access-token': token },
        );
        yield put(showNotification({ message: 'Successfully updated the role.' }));
        yield put(changeMemberRoleSuccess(memberId, roleId));
    } catch (e) {
        yield put(changeMemberRoleFailure());
    }
}

export function* watchClubEdit() {
    while (true) {
        const action = yield take(actionTypes.CHANGE_CLUB_REQUEST);
        yield fork(changeClubDetails, action.changes);
    }
}

export function* watchClubList() {
    while (true) {
        const action = yield take(actionTypes.MAKE_CLUB_LIST);
        yield fork(makeClubList, action.clubsData);
    }
}

export function* watchCreateClub() {
    while (true) {
        const action = yield take(actionTypes.CREATE_CLUB_REQUEST);
        yield fork(createClub, action);
    }
}

export function* watchJoinClub() {
    while (true) {
        const action = yield take(actionTypes.JOIN_CLUB_REQUEST);
        yield fork(joinClub, action);
    }
}

export function* watchClubsInfo() {
    while (true) {
        yield take([actionTypes.CLUBS_INFO_REQUEST, actionTypes.UPDATE_LEVEL_SUCCESS]);
        yield fork(getClubsInfo);
    }
}

export function* watchCurrentClub() {
    let task = null;
    while (true) {
        const action = yield take(actionTypes.CURRENT_CLUB_REQUEST);
        if(task) yield cancel(task);
        task = yield fork(setCurrentClub, action.clubId);
    }
}

export function* watchCurrentClubHandler() {
    while (true) {
        yield take([actionTypes.CLUBS_INFO_SUCCESS]);
        const clubId = yield select(getCurrentClubId);
        if( !!clubId ) {
            yield fork(setCurrentClub, clubId);
        }
    }
}

export function* watchCurrentClubMembers() {
    let task = null;
    while (true) {
        yield take([
            actionTypes.CURRENT_CLUB_MEMBERS_REQUEST, actionTypes.REMOVE_MEMBER_SUCCESS, actionTypes.BALANCE_TRANSFER_SUCCESS,
            actionTypes.CHANGE_MEMBER_ROLE_SUCCESS, actionTypes.CHANGE_MEMBER_STATUS_SUCCESS, actionTypes.UPDATE_SELECTED_MEMBERS_SUCCESS
        ]);
        if(task) yield cancel(task);
        task = yield fork(getCurrentClubMembers);
    }
}
export function* fetchCurrentClubMembersByInterval() {
    while (true) {
        const clubId = yield select(getCurrentClubId);
        if( !!clubId ) {
            yield fork(getCurrentClubMembers);
        }
        yield delay(config.fetchDataIntervals.clubMembers);
    }
}

export function* watchDisbandClub() {
    while (true) {
        const action = yield take(actionTypes.DISBAND_CLUB_REQUEST);
        yield fork(disbandClub, action);
    }
}

export function* watchClubData() {
    while (true) {
        const action = yield take(actionTypes.CLUB_DATA_REQUEST);
        yield fork(getClubData, action);
    }
}

export function* watchChangeMemberRole() {
    while (true) {
        const action = yield take(actionTypes.CHANGE_MEMBER_ROLE_REQUEST);
        yield fork(changeMemberRole, action);
    }
}

function* processExportDataRequest({ email, tradeRecord, from, to }) {
  try {
    const token = yield select(getCurrentClubAdminToken);
    const response = yield call(callApi, `${config.API_URL}/club/accounts/reportOnEmail`, 'GET', {
      token,
      email,
      tradeRecord,
      startDate: from,
      endDate: to,
    });
    yield put(exportDataSuccess());
    yield put(showNotification({ message: response.data.result.message }));
  } catch (e) {
    yield put(exportDataFailure(e));
    yield put(showNotification({ message: e.response.data.err.message, appearance: 'error' }));
  }
}

export function* watchExportDataRequests() {
  while (true) {
    const action = yield take(actionTypes.EXPORT_DATA_REQUEST);
    yield fork(processExportDataRequest, action);
  }
}